import React, { useState } from 'react';
import {
  P,
  Icon,
  Clickable,
  Tabs,
  Badge,
  Stack,
  IconButton,
} from '@fashionnovateam/nova-ui-toolkit';

import Page from '../Page/index';
import Header from '../Header/index';
import { useAnnouncementLogs } from '../../Lib/Hooks/help-center.hooks';
import { PageLoading } from '../PageLoading/PageLoading';
import { AnnouncementLogsPageProps, Update } from './types';
import { PortableTextRenderer } from './PortableTextRenderer';

const getAnnouncementLogBadgeColor = (type: string): 'purple' | 'green' => {
  switch (type) {
    case 'Announcement':
      return 'purple';
    case 'New':
      return 'green';
    default:
      return 'green';
  }
};

export function AnnouncementLog({ user }: AnnouncementLogsPageProps) {
  const {
    data: announcementLogs,
    isLoading,
    error,
  } = useAnnouncementLogs(user.id);
  const [tab, setTab] = useState<'All' | 'New' | 'Announcement'>('All');
  const [collapsedStates, setCollapsedStates] = useState<
    Record<string, boolean>
  >({});

  const toggleCollapse = (key: string) => {
    setCollapsedStates((prevState) => ({
      ...prevState,
      [key]: !prevState[key],
    }));
  };

  if (isLoading) {
    return <PageLoading />;
  }

  if (error) {
    return (
      <Page.Container>
        <div className="flex flex-col items-center justify-center h-full">
          <Icon name="rocket-ship" className="mb-4" />
          <P>
            Failed to load Announcement Log content. Please try again later.
          </P>
        </div>
      </Page.Container>
    );
  }

  if (!announcementLogs) return null;

  const filteredUpdates = announcementLogs.updates.filter((update: Update) => {
    if (tab === 'All') return true;
    return update.type === tab;
  });

  return (
    <Page.Container>
      <Header.Major icon="rocket-ship" title="Updates" />
      <Page.Content>
        <div className="flex flex-col max-w-2xl mx-auto md:mx-[15%] ">
          <Tabs.Group type="segment" className="mb-4">
            <Clickable onClick={() => setTab('All')}>
              <Tabs.Item type="segment" isActive={tab === 'All'}>
                All
              </Tabs.Item>
            </Clickable>
            <Clickable onClick={() => setTab('New')}>
              <Tabs.Item type="segment" isActive={tab === 'New'}>
                New
              </Tabs.Item>
            </Clickable>
            <Clickable onClick={() => setTab('Announcement')}>
              <Tabs.Item type="segment" isActive={tab === 'Announcement'}>
                Announcements
              </Tabs.Item>
            </Clickable>
          </Tabs.Group>
          <Stack direction="row" gap="sm">
            <div className="px-4">
              {filteredUpdates.map((update) => {
                const {
                  // eslint-disable-next-line @typescript-eslint/naming-convention
                  _key,
                  announcementDate,
                  type,
                  announcementLogTitle,
                  announcementLogContent,
                } = update;

                // eslint-disable-next-line no-underscore-dangle
                const isCollapsed = collapsedStates[_key];
                const badgeColor = getAnnouncementLogBadgeColor(type);

                return (
                  <div key={_key} className="border-b pb-8 mb-8">
                    <div className="grid grid-cols-1 md:grid-cols-12 gap-4">
                      <div className="flex flex-col md:flex-col items-start md:col-span-3">
                        <div className="flex flex-row md:flex-col items-start">
                          <span className="text-sm text-gray-500">
                            {new Intl.DateTimeFormat('en-US', {
                              month: 'long',
                              day: 'numeric',
                              year: 'numeric',
                              timeZone: 'UTC',
                            }).format(new Date(announcementDate))}
                          </span>
                          <Badge
                            contrast="low"
                            size="lg"
                            color={badgeColor}
                            className="ml-2 md:ml-0 mt-0 md:mt-3 inline-block"
                          >
                            {type}
                          </Badge>
                        </div>
                      </div>
                      <div className="col-span-1 md:col-span-8">
                        <h2 className="text-xl md:text-2xl font-medium">
                          {announcementLogTitle}
                        </h2>
                        {!isCollapsed && (
                          <div className="prose max-w-none text-xsm nu-text-base-secondary">
                            <PortableTextRenderer
                              value={announcementLogContent || []}
                              userId={user.id}
                            />
                          </div>
                        )}
                      </div>
                      <div className="hidden md:flex col-span-1 justify-end">
                        <IconButton
                          onClick={() => toggleCollapse(_key)}
                          className="ml-auto"
                          color="secondary"
                          icon={
                            !isCollapsed ? (
                              <Icon name="minus/minus-default" />
                            ) : (
                              <Icon name="plus/plus-default" />
                            )
                          }
                          label={!isCollapsed ? 'Collapse' : 'Expand'}
                        />
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </Stack>
        </div>
      </Page.Content>
    </Page.Container>
  );
}
